// Предложение обновить фронтенд — источник правды для окна обновления (`ConnectedVersionUpdateDialog` из
// `@srm/ui/shell/VersionUpdateDialog` подписывается через `useSyncExternalStore`).
//
// Заполняется вне React-дерева: проверкой `version.json` (`createVersionCheck`) и плагином клиента (ответ сервера
// `x-client-update: required`, `clientVersionPlugin`). Экземпляр — один на приложение, его держит модуль приложения.

export interface IVersionUpdateState {
    /** Предложение на экране. */
    readonly visible: boolean;
    /** Сервер требует обновиться: окно без «Позже». */
    readonly mandatory: boolean;
}

export const DEFAULT_VERSION_UPDATE_SNOOZE_MS = 30 * 60 * 1000;

export interface IVersionUpdateOptions {
    /** На сколько «Позже» откладывает предложение; функция читается в момент нажатия (конфиг). @default 30 минут */
    snoozeMs?: number | (() => number);
    /** Часы. @default Date.now */
    now?: () => number;
}

export interface IVersionUpdate {
    readonly getState: () => IVersionUpdateState;
    /** Подписка на смену состояния; результат — отписка (контракт `useSyncExternalStore`). */
    readonly subscribe: (listener: () => void) => () => void;
    /** Доступна новая версия. Зовётся на каждой проверке; отложенное предложение ждёт конца отсрочки. */
    readonly offer: () => void;
    /** Сервер не принимает эту версию клиента дальше — окно, которое нельзя отложить. */
    readonly require: () => void;
    /** «Позже»: спрятать на время отсрочки. Обязательное обновление не откладывается. */
    readonly later: () => void;
    /**
     * Придержать необязательное предложение (вкладка показывает экран поддержке: «Обновить» прервало бы показ):
     * видимое прячется, новое ждёт; снятие показывает отложенное. Обязательное обновление не придерживается.
     */
    readonly hold: (on: boolean) => void;
    /** Исходное состояние (тесты приложения на его экземпляре). */
    readonly reset: () => void;
}

const HIDDEN: IVersionUpdateState = { visible: false, mandatory: false };
const OFFERED: IVersionUpdateState = { visible: true, mandatory: false };
const REQUIRED: IVersionUpdateState = { visible: true, mandatory: true };

/**
 * «Позже» откладывает предложение, а не отменяет: проверка версии идёт по таймеру и после отсрочки покажет его снова.
 * Иначе отказавшийся один раз сидел бы на старом фронте до закрытия вкладки и бил бы в эндпоинты, которых на сервере
 * уже нет. Члены — функции без `this`: их можно отдавать колбэками.
 */
export function createVersionUpdate({
    snoozeMs = DEFAULT_VERSION_UPDATE_SNOOZE_MS,
    now = () => Date.now(),
}: IVersionUpdateOptions = {}): IVersionUpdate {
    let state = HIDDEN;
    let snoozedUntil = 0;
    let held = false;
    let pending = false;
    const listeners = new Set<() => void>();

    const set = (next: IVersionUpdateState) => {
        if (next === state) return;
        state = next;
        for (const listener of [...listeners]) listener();
    };

    const offer = () => {
        if (state.visible || now() < snoozedUntil) return;
        if (held) pending = true;
        else set(OFFERED);
    };

    return {
        getState: () => state,
        subscribe: (listener) => {
            listeners.add(listener);
            return () => {
                listeners.delete(listener);
            };
        },
        offer,
        require: () => set(REQUIRED),
        later: () => {
            if (!state.visible || state.mandatory) return;
            snoozedUntil = now() + (typeof snoozeMs === 'function' ? snoozeMs() : snoozeMs);
            set(HIDDEN);
        },
        hold: (on) => {
            if (held === on) return;
            held = on;
            if (on) {
                if (state === OFFERED) {
                    pending = true;
                    set(HIDDEN);
                }
                return;
            }
            if (!pending) return;
            pending = false;
            offer();
        },
        reset: () => {
            snoozedUntil = 0;
            held = false;
            pending = false;
            set(HIDDEN);
        },
    };
}
